import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Loader2,
  Search,
  X,
  Crown,
  ChevronLeft,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getAuthHeaders } from '@/hooks/useAuth';
import { LoginModal } from '@/components/LoginModal';
import { ReaderCard } from '@/components/readers/ReaderCard';
import { FilterPills } from '@/components/readers/FilterPills';
import { InviteReaderDialog } from '@/components/readers/InviteReaderDialog';
import { ReaderDetailDialog } from '@/components/readers/ReaderDetailDialog';
import { fetchAvailableReaders, fetchMyReaderBookings, fetchReaderAttention, getMyReaderProfile, type ReaderAttention } from '@/services/readerApi';
import type { Reader } from '@/types/reader';
import { useSubscription } from '@/hooks/useSubscription';
import { ReaderSectionHeader } from '@/components/readers/ReaderSectionHeader';



// ── Yearly promo banner ─────────────────────────────────────────────────
const YearlyPromo: React.FC<{
  onSubscribe: () => void;
  authed: boolean;
}> = ({ onSubscribe, authed }) => {
  return (
    // Phones stack text over the Subscribe button (it used to be hidden below md with no alternative).
    <div
      className="grid items-center grid-cols-1 sm:grid-cols-[auto_1fr_auto]"
      style={{
        marginTop: 22,
        padding: 22,
        borderRadius: 'var(--hj-r-md)',
        border: '1px solid var(--hj-blue)',
        background: 'oklch(0.70 0.18 252 / 0.08)',
        gap: 22,
      }}
    >
      {/* Reader avatar cluster — small at sm, normal at md+ */}
      <div className="hidden sm:flex" style={{ gap: -8 }}>
        <img
          src="https://casting.heyjoe.io/api/s3/upload-2024-04-22T01:06:33.106Z.png"
          alt="Shannon Holmes"
          className="hj-headshot object-cover"
          style={{ width: 48, height: 48, border: '2px solid var(--hj-blue)' }}
        />
        <div style={{ marginLeft: -10 }}>
          <img
            src="https://casting.heyjoe.io/api/s3/upload-2026-01-22T21:19:12.156Z.jpg"
            alt="Katie Dallas"
            className="hj-headshot object-cover"
            style={{ width: 48, height: 48, border: '2px solid var(--hj-blue)' }}
          />
        </div>
      </div>
      <div className="min-w-0">
        <div
          className="inline-flex mono-label"
          style={{
            padding: '2px 8px',
            borderRadius: 999,
            background: 'var(--hj-yellow)',
            color: 'oklch(0.2 0.05 80)',
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.1em',
            marginBottom: 8,
          }}
        >
          Limited time
        </div>
        <h3
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 600,
            color: 'var(--hj-ink)',
            lineHeight: 1.25,
          }}
        >
          Go yearly, save $20, plus Hey Joe credit
        </h3>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 13,
            color: 'var(--hj-ink-2)',
            lineHeight: 1.5,
          }}
        >
          Subscribe for $99/year instead of $9.99/mo, and get Hey Joe credit to
          spend on events and classes.
        </p>
      </div>
      <button
        type="button"
        onClick={onSubscribe}
        className="inline-flex justify-self-start sm:justify-self-auto items-center gap-1.5"
        style={{
          padding: '10px 18px',
          borderRadius: 999,
          background: 'var(--hj-blue)',
          border: '1px solid var(--hj-blue)',
          color: 'oklch(0.18 0.005 70)',
          fontSize: 13,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'pointer',
          flexShrink: 0,
          minHeight: 44,
        }}
      >
        <Crown size={14} strokeWidth={2} />
        {authed ? 'Subscribe — $99/yr' : 'Sign in to subscribe'}
      </button>
    </div>
  );
};

const ReadersPage: React.FC = () => {
  const { toast } = useToast();
  // ?setup=reader — the welcome email / reader landing sends creators here to
  // raise their hand; reader setup is team-assisted for now.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsReaderSetup = searchParams.get('setup') === 'reader';
  // "Become a reader" used to email the team and stop there; the profile screen
  // is self-serve now, gated by the team's approval before anyone is listed.
  const requestReaderSetup = () => navigate('/readers/profile');
  const navigate = useNavigate();
  const { isSubscriber, loading: subLoading, startSubscription } = useSubscription();

  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  const isAuthenticated = !!token;
  const [showLogin, setShowLogin] = useState(false);

  const [readers, setReaders] = useState<Reader[]>([]);
  const [loading, setLoading] = useState(true);
  // How many of this person's reader requests are still live. The list itself
  // lives on /readers/requests; this page used to keep its own copy read from
  // casting's freelancer_requests, which no longer sees anything booked here.
  const [openRequestCount, setOpenRequestCount] = useState(0);

  // Filters
  const [nameQuery, setNameQuery] = useState('');
  const [timezoneFilter, setTimezoneFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Dialogs
  const [selectedReader, setSelectedReader] = useState<Reader | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const availableTimezones = useMemo(() => {
    const set = new Set<string>();
    for (const r of readers) {
      const tz = r.freelancer_profile?.timezone;
      if (tz) set.add(tz);
    }
    return ['all', ...Array.from(set).sort()];
  }, [readers]);

  const availableCategories = useMemo(() => {
    const set = new Set<string>();
    for (const r of readers) {
      for (const c of r.freelancer_profile?.reader_categories || []) {
        if (c) set.add(c);
      }
    }
    return ['all', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [readers]);

  const filteredReaders = useMemo(() => {
    const q = nameQuery.trim().toLowerCase();
    return readers.filter((reader) => {
      if (q) {
        const fullName = `${reader.first_name || ''} ${reader.last_name || ''}`.toLowerCase();
        if (!fullName.includes(q)) return false;
      }
      const profile = reader.freelancer_profile;
      if (timezoneFilter !== 'all' && profile?.timezone !== timezoneFilter) return false;
      if (categoryFilter !== 'all' && !(profile?.reader_categories || []).includes(categoryFilter)) {
        return false;
      }
      return true;
    });
  }, [readers, nameQuery, timezoneFilter, categoryFilter]);

  // Featured first, then everyone else — admin sets featured per spec.
  const sortedReaders = useMemo(() => {
    return [...filteredReaders].sort((a, b) => {
      const af = a.freelancer_profile?.featured ? 1 : 0;
      const bf = b.freelancer_profile?.featured ? 1 : 0;
      return bf - af;
    });
  }, [filteredReaders]);

  const filtersActive = nameQuery.trim() !== '' || timezoneFilter !== 'all' || categoryFilter !== 'all';
  const clearFilters = () => {
    setNameQuery('');
    setTimezoneFilter('all');
    setCategoryFilter('all');
  };

  // Anything on the reader side that needs this person, shown as a banner at the
  // top of the page rather than behind a tab.
  const [attention, setAttention] = useState<ReaderAttention | null>(null);
  useEffect(() => { if (isAuthenticated) fetchReaderAttention().then(setAttention); }, [isAuthenticated]);
  // Someone who already has a reader profile is not asked to become one.
  const [hasReaderProfile, setHasReaderProfile] = useState<boolean | null>(null);
  useEffect(() => {
    if (!isAuthenticated) { setHasReaderProfile(false); return; }
    getMyReaderProfile().then(r => setHasReaderProfile(!!r.profile)).catch(() => setHasReaderProfile(false));
  }, [isAuthenticated]);

  const loadOpenRequestCount = useCallback(async () => {
    try {
      const mine = await fetchMyReaderBookings();
      setOpenRequestCount(mine.filter(b => b.status === 'requesting' || b.status === 'pending_reader' || b.status === 'booked').length);
    } catch { /* the count is a convenience */ }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    Promise.all([
      fetchAvailableReaders().then(setReaders).catch(() => setReaders([])),
      loadOpenRequestCount(),
    ]).finally(() => setLoading(false));
  }, [isAuthenticated]);

  const refresh = () => { loadOpenRequestCount(); };

  const handleInvite = (reader: Reader) => { setSelectedReader(reader); setShowInviteDialog(true); };
  const handleInfo = (reader: Reader) => { setSelectedReader(reader); setShowDetailDialog(true); };

  // Logged-out state — keep the same upsell pattern, restyled.
  if (!isAuthenticated) {
    return (
      <main
        className="mx-auto"
        style={{
          maxWidth: 1120,
          padding: '24px 16px 96px',
          background: 'var(--hj-bg)',
          color: 'var(--hj-ink)',
        }}
      >
        <SignedOutHero onSignIn={() => setShowLogin(true)} />
        <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
      </main>
    );
  }

  return (
    <main
      className="mx-auto"
      style={{
        maxWidth: 1120,
        padding: '0 16px 96px',
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
      {attention && attention.total > 0 && (() => {
        const lines: { text: string; tab: string }[] = [];
        if (attention.askedByName) lines.push({ text: attention.askedByName === 1 ? 'Someone asked for you by name and is waiting for your answer.' : `${attention.askedByName} people asked for you by name and are waiting.`, tab: 'reading' });
        if (attention.readersWaiting) lines.push({ text: attention.readersWaiting === 1 ? 'A reader is available for your request. Pick and book.' : `${attention.readersWaiting} of your requests have readers waiting to be picked.`, tab: 'sent' });
        if (attention.openRequests) lines.push({ text: `${attention.openRequests} open ${attention.openRequests === 1 ? 'request' : 'requests'} you could take.`, tab: 'take' });
        if (attention.unreadMessages) lines.push({ text: `${attention.unreadMessages} unread ${attention.unreadMessages === 1 ? 'message' : 'messages'} on your sessions.`, tab: 'reading' });
        const first = lines[0];
        return (
          <div className="flex items-center justify-between gap-4 flex-wrap" style={{ margin: '16px 0 0', padding: '14px 16px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-yellow)', color: '#0A0A0A' }}>
            <div style={{ fontSize: 14, lineHeight: 1.5 }}>
              <strong>{first.text}</strong>
              {lines.slice(1).map(l => <span key={l.tab + l.text}> {l.text}</span>)}
            </div>
            <Link to={`/readers/requests?tab=${first.tab}`} style={{ padding: '9px 18px', borderRadius: 999, background: '#0A0A0A', color: 'var(--hj-yellow-ink)', fontSize: 13, fontWeight: 700, textDecoration: 'none', flexShrink: 0 }}>
              Open it
            </Link>
          </div>
        );
      })()}

      {wantsReaderSetup && !hasReaderProfile && (
        <div className="flex items-center justify-between gap-4 flex-wrap" style={{ margin: '16px 0 0', padding: '14px 16px', borderRadius: 'var(--hj-r-md)', background: 'rgb(65 113 249 / 0.08)', border: '1px solid rgb(65 113 249 / 0.25)' }}>
          <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
            <strong>Want to read for actors?</strong> Set up your reader profile. The Hey Joe team reviews it, then you're on the list.
          </p>
          <button type="button" onClick={requestReaderSetup} style={{ padding: '9px 18px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0 }}>
            Set up my reader profile
          </button>
        </div>
      )}

      {/* Breadcrumb */}
      <div
        className="flex items-center gap-2.5"
        style={{ padding: '20px 0 10px', fontSize: 13 }}
      >
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5"
          style={{
            background: 'transparent',
            border: 0,
            color: 'var(--hj-ink-3)',
            cursor: 'pointer',
            padding: 0,
            fontFamily: 'inherit',
            fontSize: 13,
          }}
        >
          <ChevronLeft size={12} strokeWidth={2} />
          Home
        </button>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>Readers</span>
      </div>

      {/* Section header + tabs, shared with /readers/requests so the two read as
          one tabbed section rather than two unrelated screens. */}
      <ReaderSectionHeader
        active="find"
        requestCount={openRequestCount}
        actions={(
          <button
            type="button"
            onClick={requestReaderSetup}
            className="inline-flex items-center"
            style={{
              padding: '8px 16px', borderRadius: 999, background: 'var(--hj-beige-ink)',
              border: '1px solid var(--hj-beige-ink)', color: 'var(--hj-beige)', fontSize: 13,
              fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', minHeight: 44,
            }}
          >
            {hasReaderProfile ? 'My reader profile' : 'Become a reader'}
          </button>
        )}
      />

      <>
      {/* Yearly promo banner — non-subscribers */}
      {!isSubscriber && !subLoading && (
        <YearlyPromo onSubscribe={() => startSubscription('yearly')} authed />
      )}

      {/* Filters row */}
      {!loading && readers.length > 0 && (
        <>
          <div
            className="flex items-center flex-wrap md:flex-nowrap gap-2.5 overflow-x-auto"
            style={{ padding: '24px 0 16px' }}
          >
            <div className="relative flex-1 min-w-[200px]">
              <Search
                size={14}
                strokeWidth={1.8}
                style={{
                  position: 'absolute',
                  left: 14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--hj-ink-4)',
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                value={nameQuery}
                onChange={(e) => setNameQuery(e.target.value)}
                placeholder="Search by name…"
                className="w-full"
                style={{
                  padding: '10px 14px 10px 36px',
                  borderRadius: 999,
                  background: 'var(--hj-surface-2)',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink)',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  outline: 'none',
                  minHeight: 44,
                }}
              />
            </div>
            <FilterPills
              label="Timezone"
              value={timezoneFilter}
              options={availableTimezones}
              onChange={setTimezoneFilter}
            />
            <FilterPills
              label="Category"
              value={categoryFilter}
              options={availableCategories}
              onChange={setCategoryFilter}
            />
            {filtersActive && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1"
                style={{
                  padding: '8px 12px',
                  borderRadius: 999,
                  background: 'transparent',
                  border: 0,
                  color: 'var(--hj-ink-3)',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  flexShrink: 0,
                  minHeight: 44,
                }}
              >
                <X size={14} /> Clear
              </button>
            )}
            <div
              className="ml-auto flex-shrink-0"
              style={{ fontSize: 13, color: 'var(--hj-ink-3)' }}
            >
              <span
                className="tabular"
                style={{ color: 'var(--hj-ink)', fontWeight: 600 }}
              >
                {sortedReaders.length}
              </span>{' '}
              of {readers.length} readers
            </div>
          </div>
        </>
      )}

      {/* Reader grid */}
      {loading ? (
        <div
          className="flex items-center justify-center"
          style={{ padding: '64px 0' }}
        >
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--hj-ink-4)' }} />
        </div>
      ) : readers.length === 0 ? (
        <div
          className="text-center"
          style={{ padding: '64px 0', color: 'var(--hj-ink)' }}
        >
          <p style={{ margin: 0 }}>No readers available at the moment.</p>
          <p className="mono-label" style={{ marginTop: 6 }}>Check back soon</p>
        </div>
      ) : sortedReaders.length === 0 ? (
        <div
          className="text-center"
          style={{
            padding: 48,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
            color: 'var(--hj-ink)',
          }}
        >
          <p style={{ margin: 0 }}>No readers match your filters.</p>
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex items-center"
            style={{
              marginTop: 14,
              padding: '7px 14px',
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--hj-hairline)',
              color: 'var(--hj-ink-2)',
              fontSize: 13,
              fontFamily: 'inherit',
              cursor: 'pointer',
              minHeight: 44,
            }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          {sortedReaders.map((reader) => (
            <ReaderCard
              key={reader._id}
              reader={reader}
              onInvite={handleInvite}
              onInfo={handleInfo}
            />
          ))}
        </div>
      )}

      </>

      {/* Footer CTA */}
      <div
        className="text-center"
        style={{ marginTop: 40, fontSize: 13, color: 'var(--hj-ink)' }}
      >
        {hasReaderProfile ? (
          <>Your listing is yours to change.{' '}<Link to="/readers/profile" style={{ color: 'var(--hj-yellow-ink)', textDecoration: 'none' }}>Edit your reader profile</Link></>
        ) : (
          <>Interested in being listed?{' '}<Link to="/readers/profile" style={{ color: 'var(--hj-yellow-ink)', textDecoration: 'none' }}>Set up your reader profile</Link></>
        )}
      </div>

      {/* Dialogs */}
      <InviteReaderDialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        reader={selectedReader}
        onInviteSent={refresh}
      />
      <ReaderDetailDialog
        open={showDetailDialog}
        onClose={() => setShowDetailDialog(false)}
        reader={selectedReader}
        onInvite={handleInvite}
      />
    </main>
  );
};

// Logged-out hero — restyled to brand tokens. Keeps the dual-reader promo.
const SignedOutHero: React.FC<{ onSignIn: () => void }> = ({ onSignIn }) => {
  return (
    <>
      <div
        className="flex items-center gap-2.5"
        style={{ padding: '4px 0 10px', fontSize: 13 }}
      >
        <Link
          to="/"
          className="inline-flex items-center gap-1.5"
          style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}
        >
          <ChevronLeft size={12} strokeWidth={2} />
          Home
        </Link>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>Readers</span>
      </div>

      <div style={{ margin: '4px 0 0', padding: '22px 24px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
        <div className="mono-label" style={{ marginBottom: 10, color: 'var(--hj-beige-ink-2)' }}>Reader Marketplace</div>
        <h1
          className="font-hj-display m-0"
          style={{
            fontSize: 'clamp(40px, 5.6vw, 68px)',
            lineHeight: 1,
            color: 'var(--hj-beige-ink)',
            letterSpacing: '-0.01em',
          }}
        >
          Book a reader.
        </h1>
        <p
          style={{
            margin: '14px 0 0',
            color: 'var(--hj-beige-ink)',
            fontSize: 15,
            maxWidth: 640,
            lineHeight: 1.5,
          }}
        >
          Practice your audition with a real person. Browse experienced readers
          and coaches ready to help you nail your next role.
        </p>
      </div>

      <div
        className="grid items-center"
        style={{
          marginTop: 28,
          padding: 26,
          borderRadius: 'var(--hj-r-md)',
          background: 'var(--hj-beige)',
          color: 'var(--hj-beige-ink)',
          gridTemplateColumns: 'auto 1fr auto',
          gap: 22,
        }}
      >
        <div className="hidden sm:flex">
          <img
            src="https://casting.heyjoe.io/api/s3/upload-2024-04-22T01:06:33.106Z.png"
            alt="Shannon Holmes"
            className="hj-headshot object-cover"
            style={{ width: 56, height: 56, border: '2px solid var(--hj-blue)' }}
          />
          <div style={{ marginLeft: -12 }}>
            <img
              src="https://casting.heyjoe.io/api/s3/upload-2026-01-22T21:19:12.156Z.jpg"
              alt="Katie Dallas"
              className="hj-headshot object-cover"
              style={{ width: 56, height: 56, border: '2px solid var(--hj-blue)' }}
            />
          </div>
        </div>
        <div className="min-w-0">
          <div
            className="inline-flex mono-label"
            style={{
              padding: '2px 8px',
              borderRadius: 999,
              background: 'var(--hj-yellow)',
              color: 'oklch(0.2 0.05 80)',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.1em',
              marginBottom: 10,
            }}
          >
            Limited time
          </div>
          <h3
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 600,
              color: 'var(--hj-beige-ink)',
              lineHeight: 1.25,
            }}
          >
            Save 17% and get Hey Joe credit
          </h3>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 13.5,
              color: 'var(--hj-beige-ink-2)',
              lineHeight: 1.5,
            }}
          >
            Sign in and subscribe yearly at $99. Your year comes with Hey Joe
            credit to spend on events and classes.
          </p>
        </div>
        <button
          type="button"
          onClick={onSignIn}
          className="hidden md:inline-flex items-center gap-1.5"
          style={{
            padding: '10px 18px',
            borderRadius: 999,
            background: 'var(--hj-blue)',
            border: '1px solid var(--hj-blue)',
            color: 'oklch(0.18 0.005 70)',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
            flexShrink: 0,
            minHeight: 44,
          }}
        >
          <Crown size={14} strokeWidth={2} />
          Sign in to subscribe
        </button>
      </div>

      <div
        className="text-center"
        style={{ marginTop: 36, color: 'var(--hj-ink)', fontSize: 14 }}
      >
        <p style={{ margin: '0 0 14px' }}>Sign in to browse available readers.</p>
        <button
          type="button"
          onClick={onSignIn}
          className="inline-flex items-center"
          style={{
            padding: '10px 22px',
            borderRadius: 999,
            background: 'var(--hj-blue)',
            border: '1px solid var(--hj-blue)',
            color: 'oklch(0.18 0.005 70)',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          Sign in
        </button>
      </div>
    </>
  );
};

export default ReadersPage;